import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post } from '../utils/api';
import { useAuth } from './useAuth';
import type {
  PortfolioSummary,
  PortfolioTimeseriesPoint,
  TradingPosition,
  UserBalance,
} from '../types/portfolio';

/**
 * Hook to fetch portfolio summary (balance, open positions count, P&L)
 */
type PortfolioHookOptions = {
  enabled?: boolean;
};

export function usePortfolioSummary(options?: PortfolioHookOptions) {
  const { user } = useAuth();
  const isEnabled = !!user && (options?.enabled ?? true);

  return useQuery({
    queryKey: ['portfolio', 'summary', user?.id],
    queryFn: async () => {
      const response = await get<PortfolioSummary>('/api/portfolio');
      return response.data as any;
    },
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in memory
    refetchOnMount: false, // Use cached data on mount
    refetchOnWindowFocus: false, // Don't refetch on focus
    select: (data: any) => {
      // Unwrap the nested data structure: { success, data: { balance, ... }, timestamp }
      return data?.data || data;
    },
  });
}

/**
 * Hook to fetch portfolio timeseries data for charting
 */
export function usePortfolioTimeseries(
  range: '7d' | '30d' | '90d' = '30d',
  options?: PortfolioHookOptions
) {
  const { user } = useAuth();
  const isEnabled = !!user && (options?.enabled ?? true);

  return useQuery({
    queryKey: ['portfolio', 'timeseries', range, user?.id],
    queryFn: async () => {
      const response = await get<PortfolioTimeseriesPoint[]>(`/api/portfolio/timeseries?range=${range}`);
      return response.data as any;
    },
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in memory
    refetchOnMount: false, // Use cached data on mount
    refetchOnWindowFocus: false, // Don't refetch on focus
    select: (data: any) => data?.data || data,
  });
}

/**
 * Hook to fetch user's trading positions
 */
interface PositionsResponse {
  positions: TradingPosition[];
  maxPositions: number;
}

export function usePositions(status?: 'open' | 'closed', options?: PortfolioHookOptions) {
  const { user } = useAuth();
  const isEnabled = !!user && (options?.enabled ?? true);

  return useQuery<PositionsResponse>({
    queryKey: ['portfolio', 'positions', status, user?.id],
    queryFn: async () => {
      const url = status ? `/api/portfolio/positions?status=${status}` : '/api/portfolio/positions';
      const response = await get<TradingPosition[]>(url);
      return response.data as any;
    },
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in memory
    refetchOnMount: false, // Use cached data on mount
    refetchOnWindowFocus: false, // Don't refetch on focus
    select: (data: any) => {
      // Backend returns { success, data: [...positions], maxPositions, timestamp }
      const positions = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
      const maxPositions = data?.maxPositions ?? 3;
      return { positions, maxPositions } as PositionsResponse;
    },
  });
}

/**
 * Hook to close a trading position
 */
export function useClosePosition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (positionId: string) => {
      const response = await post<TradingPosition>('/api/portfolio/close', { positionId });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all portfolio-related queries
      queryClient.invalidateQueries({ queryKey: ['portfolio'] });
      queryClient.invalidateQueries({ queryKey: ['balance'] });
    },
  });
}

/**
 * Hook to open a new trading position
 */
export function useOpenPosition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      stockSymbol: string;
      category: string;
      direction: 'long' | 'short';
      investedCoins: number;
    }) => {
      const response = await post<TradingPosition>('/api/portfolio/positions', params);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all portfolio-related queries
      queryClient.invalidateQueries({ queryKey: ['portfolio'] });
      queryClient.invalidateQueries({ queryKey: ['balance'] });
    },
  });
}

/**
 * Hook to fetch user's coin balance
 */
export function useBalance(options?: PortfolioHookOptions) {
  const { user } = useAuth();
  const isEnabled = !!user && (options?.enabled ?? true);

  return useQuery({
    queryKey: ['balance', user?.id], // Include user ID in key to prevent cross-user caching
    queryFn: async () => {
      const response = await get<UserBalance>('/api/balance');
      return response.data as any; // Backend wraps in { success, data, timestamp }
    },
    enabled: isEnabled, // Only fetch when user is logged in and allowed by caller
    staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in memory
    refetchOnMount: false, // Use cached data on mount
    refetchOnWindowFocus: false, // Don't refetch on focus
    select: (data: any) => {
      // Unwrap the nested data structure: { success, data: { balanceCoins }, timestamp }
      return data?.data || data; // Return data.data if it exists, otherwise return data as-is
    },
  });
}
